/**
 * CompleteStep - Completion confirmation for Bank Entry workflow
 */

import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useBankEntryStore } from '../../stores/bankEntryStore';
import { CheckCircle2, RefreshCw, XCircle, Download, Loader2 } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL;

export const CompleteStep: React.FC = () => {
  const token = localStorage.getItem('auth_token');
  const [downloading, setDownloading] = useState(false);

  const {
    createdVouchers,
    failedVouchers,
    paymentCount,
    receiptCount,
    contraCount,
    voucherResults,
    rows,
    selectedCompany,
    selectedBankAccount,
    reset
  } = useBankEntryStore();

  const hasSuccess = createdVouchers > 0;

  // Build result map for quick lookup
  const resultMap = new Map(voucherResults.map(r => [r.rowIndex, r]));

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Merge rows with their results
      const rowsWithResults = rows.map(row => {
        const result = resultMap.get(row.rowIndex);
        return {
          rowIndex: row.rowIndex,
          date: row.date,
          narration: row.narration,
          debit: row.debit,
          credit: row.credit,
          ledger: row.ledger,
          vchType: row.vchType,
          status: result?.status || 'pending',
          voucherNumber: result?.voucherNumber || null,
          error: result?.error || null,
        };
      });

      const response = await fetch(`${API_BASE}/api/bank-entry/download-results`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company: selectedCompany,
          bankLedger: selectedBankAccount,
          rows: rowsWithResults,
        }),
      });

      if (!response.ok) throw new Error('Download failed');

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || 'bank_entry_results.xlsx';

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('[BankEntry] Downloaded results file:', filename);
    } catch (error) {
      console.error('[BankEntry] Download error:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardContent className="pt-12 pb-8 text-center">
        {hasSuccess ? (
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
        ) : (
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        )}
        <h2 className="text-2xl font-bold mb-2">
          {hasSuccess ? 'Vouchers Created Successfully!' : 'Voucher Creation Failed'}
        </h2>
        <p className="text-muted-foreground mb-4">
          {createdVouchers} vouchers created in Tally
        </p>

        {/* Voucher type breakdown */}
        {createdVouchers > 0 && (
          <div className="flex justify-center gap-3 mb-4">
            {paymentCount > 0 && (
              <Badge variant="outline" className="text-red-600 border-red-200">
                {paymentCount} Payment{paymentCount !== 1 ? 's' : ''}
              </Badge>
            )}
            {receiptCount > 0 && (
              <Badge variant="outline" className="text-green-600 border-green-200">
                {receiptCount} Receipt{receiptCount !== 1 ? 's' : ''}
              </Badge>
            )}
            {contraCount > 0 && (
              <Badge variant="outline" className="text-blue-600 border-blue-200">
                {contraCount} Contra
              </Badge>
            )}
          </div>
        )}

        {failedVouchers > 0 && (
          <p className="text-red-500 mb-4">
            {failedVouchers} voucher{failedVouchers !== 1 ? 's' : ''} failed
          </p>
        )}

        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={handleDownload} disabled={downloading}>
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download Results
          </Button>
          <Button onClick={reset}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Process Another Statement
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompleteStep;
